import React, { useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Image,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  useColorModeValue,
  useColorMode,
  useOutsideClick,
  Avatar,
  Badge,
  Collapse,
  Container,
  Divider,
  Spinner,
} from "@chakra-ui/react";
import { ChevronDownIcon, HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import ThemeAwareLogo from "../common/ThemeAwareLogo";
import SimpleLogo from "../common/SimpleLogo";
import DirectLogo from "../common/DirectLogo";
import { useAuth } from "../../hooks/useAuth";
import type { User } from "../../contexts/AuthContext";
import DarkModeToggle from "../common/DarkModeToggle";
import { useSubscription } from "../../contexts/SubscriptionContext";
import "./Navbar.css";

const Navbar = ({ currentPath = "" }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const { subscriptionStatus } = useSubscription();
  // Determine if we are on the homepage to adjust navbar styling
  const isHomePage = currentPath === "/";
  const isAboutPage = currentPath === "/about";
  // Overlay-style navbar for pages that need content/background to reach the very top behind the nav
  const isOverlayNav = isHomePage || isAboutPage;
  const { isOpen, onToggle } = useDisclosure();
  const dropdownRef = useRef(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Enhanced user data with subscription info (guard when user is null)
  const userWithPlan = user
    ? {
        ...user,
        subscriptionPlan: (() => {
          const tier = (subscriptionStatus?.subscription?.tier || subscriptionStatus?.usage_limits?.tier || 'free') as string;
          return tier.charAt(0).toUpperCase() + tier.slice(1);
        })(),
      }
    : undefined;

  // Theme-aware nav fill using design-system variables
  const bg = 'var(--color-bg-tertiary)';
  const isPricingPage = currentPath.startsWith('/pricing');
  const borderColor = 'var(--color-border)';
  const textColor = 'var(--color-text)';
  const hoverColor = useColorModeValue("blue.500", "blue.300");
  const dropdownBg = 'var(--color-dropdown-bg)';
  const dropdownBorderColor = 'var(--color-border)';
  const dropdownHoverBg = 'var(--color-hover-bg)';
  const menuButtonHoverBg = 'var(--color-hover-bg)';
  const menuButtonActiveBg = 'var(--color-hover-bg)';

  // Style for the active navigation link
  const activeLinkStyle = {
    bg: 'var(--color-hover-bg)',
    color: 'var(--color-primary)',
    fontWeight: "bold",
    borderRadius: "md",
  };

  // Custom menu item component
  const CustomMenuItem = ({ to, icon, onClick, isRed, children }) => (
    <Box
      as={RouterLink}
      to={to}
      display="flex"
      alignItems="center"
      px={3}
      py={2}
      borderRadius="md"
      color={isRed ? "red.500" : undefined}
      fontWeight={isRed ? "medium" : "normal"}
      _hover={{ bg: dropdownHoverBg, textDecoration: "none" }}
      onClick={() => {
        if (onClick) onClick();
      }}
    >
      {icon && <Box mr={2}>{icon}</Box>}
      <Text>{children}</Text>
    </Box>
  );

  // Logout button (not a link)
  const LogoutMenuItem = ({ icon, children }) => (
    <Button
      variant="unstyled"
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
      width="full"
      px={3}
      py={2}
      borderRadius="md"
      color="red.500"
      fontWeight="medium"
      _hover={{ bg: dropdownHoverBg }}
      onClick={handleLogout}
      disabled={isLoggingOut}
    >
      {isLoggingOut ? (
        <>
          <Spinner size="sm" mr={2} />
          <Text>Logging out...</Text>
        </>
      ) : (
        <>
          {icon && <Box mr={2}>{icon}</Box>}
          <Text>{children}</Text>
        </>
      )}
    </Button>
  );

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
      setIsLoggingOut(false);
    }
  };

  return (
    <Box
      as="nav"
      py={4}
      px={4}
      position={isOverlayNav ? "fixed" : "sticky"}
      top={0}
      left={0}
      w="100%"
      zIndex={50}
      bg="transparent"
    >
      <Container
        maxW="1400px"
        mx="auto"
        bg={bg}
        borderRadius="lg"
        boxShadow="md"
        px={6}
        py={2}
      >
      <Flex
        align="center"
        justify="space-between"
        wrap="nowrap"
        width="100%"
      >
        <Flex align="center">
          <Box
            as={RouterLink}
            to="/dashboard"
            _hover={{ textDecoration: "none" }}
            display="flex"
            alignItems="center"
            minW="max-content"
            flexShrink={0}
          >
            <DirectLogo />
            <Text
              ml={2}
              fontSize="xl"
              fontWeight="bold"
              className="navbarAppName"
              whiteSpace="nowrap"
            >
              LearnYourself
            </Text>
          </Box>
        </Flex>

        {/* Mobile menu button */}
        <IconButton
          display={{ base: "flex", md: "none" }}
          onClick={onToggle}
          icon={
            isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
          }
          variant="ghost"
          aria-label="Toggle Navigation"
          position="relative"
          zIndex={2}
        />

        {/* Desktop Navigation */}
        <Box
          display={{ base: "none", md: "flex" }}
          width={{ base: "full", md: "auto" }}
          alignItems="center"
          justifyContent="flex-end"
          mt={{ base: 4, md: 0 }}
          pr={0}
        >
          {isAuthenticated() ? (
            <Flex
              direction={{ base: "column", md: "row" }}
              align={{ base: "flex-start", md: "center" }}
              width={{ base: "full", md: "auto" }}
            >
              {/* is authenticated */}
              {!currentPath.includes("/chat") && (
                <HStack
                  spacing={6}
                  mr={6}
                  display={{ base: "none", md: "flex" }}
                >
                  <Box
                    as={RouterLink}
                    to="/chat"
                    color={textColor}
                    fontWeight="medium"
                    _hover={{ color: hoverColor, textDecoration: "none" }}
                    position="relative"
                    px={3}
                    py={1}
                    {...(currentPath === "/chat" ? activeLinkStyle : {})}
                  >
                    Chat
                    {/* <Badge
                      colorScheme="green"
                      position="absolute"
                      top="-1"
                      right="-4"
                      fontSize="xs"
                    >
                      New
                    </Badge> */}
                  </Box>
                  <Box
                    as={RouterLink}
                    to="/courses"
                    color={textColor}
                    fontWeight="medium"
                    _hover={{ color: hoverColor, textDecoration: "none" }}
                    px={3}
                    py={1}
                    {...(currentPath === "/courses" ? activeLinkStyle : {})}
                  >
                    Courses
                  </Box>
                  <Box
                    as={RouterLink}
                    to="/documents"
                    color={textColor}
                    fontWeight="medium"
                    _hover={{ color: hoverColor, textDecoration: "none" }}
                    px={3}
                    py={1}
                    {...(currentPath === "/documents" ? activeLinkStyle : {})}
                  >
                    Documents
                  </Box>
                  {/* <Box
                    as={RouterLink}
                    to="/streaming-test"
                    color={textColor}
                    fontWeight="medium"
                    _hover={{ color: hoverColor, textDecoration: "none" }}
                    position="relative"
                  >
                    Streaming Test
                    <Badge
                      colorScheme="purple"
                      position="absolute"
                      top="-1"
                      right="-4"
                      fontSize="xs"
                    >
                      Beta
                    </Badge>
                  </Box> */}
                </HStack>
              )}

              {/* User Menu */} 
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  px={4}
                  py={2}
                  rounded="full"
                  variant="ghost"
                  _hover={{ bg: menuButtonHoverBg }}
                  _active={{ bg: menuButtonActiveBg }}
                  display={{ base: "none", md: "flex" }}
                >
                  <Flex align="center">
                    <Avatar
                      size="sm"
                      name={user?.username || "User"}
                      src={user?.avatar || undefined}
                      bg="blue.500"
                      color="white"
                      mr={2}
                    />
                    <Text>{user?.username}</Text>
                  </Flex>
                </MenuButton>
                <MenuList
                  className="navbarDropdown"
                  bg={dropdownBg}
                  borderColor={dropdownBorderColor}
                  ref={dropdownRef}
                  zIndex={10}
                  minWidth="284px"
                  p={2}
                  borderRadius="12px"
                >
                  <Box className="dropdownEmail" px={4} py={3} textAlign="center">
                    <Text fontSize="sm" color="var(--color-sidebar-text-muted)" fontWeight="500">
                      {userWithPlan?.email || 'user@example.com'}
                    </Text>
                  </Box>
                  
                  <Divider my={2} />
                  
                  <MenuItem 
                    className="dropdownItem"
                    onClick={() => navigate('/profile')}
                    display="flex"
                    alignItems="center"
                    gap={3}
                    px={4}
                    py={3}
                    borderRadius="8px"
                    bg="transparent"
                    _hover={{ bg: dropdownHoverBg }}
                    transition="all 0.2s"
                  >
                    <Box as="svg" className="dropdownIcon" w="16px" h="16px" viewBox="0 0 20 20" fill="currentColor" opacity={0.7}>
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </Box>
                    <Text fontSize="sm">My Profile</Text>
                  </MenuItem>
                  
                  <MenuItem 
                    className="dropdownItem"
                    onClick={() => navigate('/documents')}
                    display="flex"
                    alignItems="center"
                    gap={3}
                    px={4}
                    py={3}
                    borderRadius="8px"
                    bg="transparent"
                    _hover={{ bg: dropdownHoverBg }}
                    transition="all 0.2s"
                  >
                    <Box as="svg" className="dropdownIcon" w="16px" h="16px" viewBox="0 0 20 20" fill="currentColor" opacity={0.7}>
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </Box>
                    <Text fontSize="sm">Documents</Text>
                  </MenuItem>
                  
                  <MenuItem 
                    className="dropdownItem"
                    onClick={() => navigate('/billing')}
                    display="flex"
                    alignItems="center"
                    gap={3}
                    px={4}
                    py={3}
                    borderRadius="8px"
                    bg="transparent"
                    _hover={{ bg: dropdownHoverBg }}
                    transition="all 0.2s"
                  >
                    <Box as="svg" className="dropdownIcon" w="16px" h="16px" viewBox="0 0 24 24" fill="currentColor" opacity={0.7}>
                      <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                    </Box>
                    <Text fontSize="sm">Billing & Subscription</Text>
                  </MenuItem>
                  
                  {user?.is_superuser && (
                    <MenuItem 
                      className="dropdownItem"
                      onClick={() => navigate('/diagnostics')}
                      display="flex"
                      alignItems="center"
                      gap={3}
                      px={4}
                      py={3}
                      borderRadius="8px"
                      bg="transparent"
                      _hover={{ bg: dropdownHoverBg }}
                      transition="all 0.2s"
                    >
                      <Box as="svg" className="dropdownIcon" w="16px" h="16px" viewBox="0 0 24 24" fill="currentColor" opacity={0.7}>
                        <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                      </Box>
                      <Text fontSize="sm">Database Diagnostics</Text>
                    </MenuItem>
                  )}
                  
                  <Divider my={2} />
                  
                  <Box className="darkModeItem" px={3} py={2}>
                    <Box position="relative" display="inline-block" w="full">
                      <input
                        type="checkbox"
                        id="navbarDarkModeToggle"
                        checked={colorMode === 'dark'}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleColorMode();
                        }}
                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                        aria-label="Toggle dark mode"
                      />
                      
                      <Flex
                        as="label"
                        htmlFor="navbarDarkModeToggle"
                        className="dark-mode-toggle"
                        cursor="pointer"
                        align="center"
                        position="relative"
                        width="100%"
                        height="40px"
                        borderRadius="20px"
                        bg={colorMode === 'dark' ? 'var(--color-gray-700)' : 'var(--color-gray-300)'}
                        transition="all 0.3s ease"
                        overflow="visible"
                        _hover={{ opacity: 0.85 }}
                        userSelect="none"
                      >
                        <Box
                          className="toggle-circle"
                          position="absolute"
                          width="50%"
                          height="36px"
                          bg={colorMode === 'dark' ? 'var(--chakra-colors-gray-800)' : 'white'}
                          borderRadius="16px"
                          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                          left={colorMode === 'dark' ? 'calc(50% - 2px)' : '2px'}
                          top="2px"
                          boxShadow={colorMode === 'dark' 
                            ? '0 0 8px rgba(255, 255, 255, 0.3), 0 0 16px rgba(255, 255, 255, 0.15), 0 2px 5px rgba(0,0,0,0.1)' 
                            : '0 2px 10px rgba(0,0,0,0.15)'
                          }
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          zIndex="2"
                        >
                          {colorMode === 'dark' ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                              <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1 -8.313 -12.454z" />
                              <path d="M17 4a2 2 0 0 0 2 2a2 2 0 0 0 -2 2a2 2 0 0 0 -2 -2a2 2 0 0 0 2 -2" />
                              <path d="M19 11h2m-1 -1v2" />
                            </svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="black">
                              <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2"/>
                              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          )}
                        </Box>
                        
                        <Flex width="100%" height="100%" position="relative" zIndex={0} pointerEvents="none">
                          <Flex
                            align="center"
                            justify="center"
                            flex={1}
                            color={colorMode !== 'dark' ? 'black' : '#666'}
                            fontWeight={colorMode !== 'dark' ? '700' : '400'}
                            transition="all 0.3s ease"
                          >
                            <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.5px">
                              Day
                            </Text>
                          </Flex>

                          <Flex
                            align="center"
                            justify="center"
                            flex={1}
                            color={colorMode === 'dark' ? 'white' : '#666'}
                            fontWeight={colorMode === 'dark' ? '700' : '400'}
                            transition="all 0.3s ease"
                          >
                            <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.5px">
                              Night
                            </Text>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Box>
                  </Box>
                  
                  <Divider my={2} />
                  
                  <MenuItem 
                    className="dropdownItem"
                    onClick={handleLogout}
                    display="flex"
                    alignItems="center"
                    gap={3}
                    px={4}
                    py={3}
                    borderRadius="8px"
                    bg="transparent"
                    color="red.500"
                    _hover={{ bg: dropdownHoverBg }}
                    transition="all 0.2s"
                    isDisabled={isLoggingOut}
                    opacity={isLoggingOut ? 0.5 : 1}
                  >
                    {isLoggingOut ? (
                      <Spinner size="xs" />
                    ) : (
                      <Box as="svg" className="dropdownIcon" w="16px" h="16px" viewBox="0 0 20 20" fill="currentColor" opacity={0.7}>
                        <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                      </Box>
                    )}
                    <Text fontSize="sm">{isLoggingOut ? "Logging out..." : "Logout"}</Text>
                  </MenuItem>
                </MenuList>
              </Menu>
            </Flex>
          ) : (            
            <Flex
              direction={{ base: "column", md: "row" }}
              align={{ base: "flex-start", md: "center" }}
              width="full"
            >
              {/* Non-logged in user menu */}
              <Box
                as={RouterLink}
                to="/pricing"
                color={textColor}
                fontWeight="medium"
                _hover={{ color: hoverColor, textDecoration: "none" }}
                py={2}
                mr={{ base: 0, md: 4 }}
                mb={{ base: 4, md: 0 }}
                px={3}
                {...(currentPath === "/pricing" ? activeLinkStyle : {})}
              >
                Pricing
              </Box>
              <Box
                as={RouterLink}
                to="/login"
                color={textColor}
                fontWeight="medium"
                _hover={{ color: hoverColor, textDecoration: "none" }}
                py={2}
                mr={{ base: 0, md: 4 }}
                mb={{ base: 4, md: 0 }}
              >
                Login
              </Box>
              <Button
                as={RouterLink}
                to="/signup"
                colorScheme="blue"
                _hover={{ shadow: "md" }}
              >
                Sign up
              </Button>
            </Flex>
          )}
        </Box>
      </Flex>

      {/* Mobile Navigation Menu */}
      <Collapse in={isOpen} animateOpacity>
        <Box
          pb={4}
          mt={4}
          rounded="md"
          shadow={'md'}
          bg={bg}
          display={{ base: "block", md: "none" }}
          position="relative"
        >
          <VStack alignItems="stretch" spacing={1}>
            {isAuthenticated() ? (
              <>
                {/* User Profile Section - Clickable */}
                <Box px={3} py={3} mb={2}>
                  <Flex align="center">
                    <Avatar
                      size="sm"
                      name={user?.username || "User"}
                      src={user?.avatar || undefined}
                      bg="blue.500"
                      color="white"
                      mr={3}
                    />
                    <Box>
                      <Text fontWeight="bold" fontSize="sm">{user?.username}</Text>
                      <Text fontSize="xs" color="var(--color-sidebar-text-muted)">{user?.email}</Text>
                    </Box>
                  </Flex>
                </Box>
                <Divider mb={2} />
                
            <Box
              as={RouterLink}
              to="/chat"
              py={2}
              px={3}
              _hover={{ color: hoverColor, textDecoration: "none" }}
              {...(currentPath === "/chat" ? activeLinkStyle : {})}
            >
              Chat
              {/* <Badge colorScheme="green" ml={2} fontSize="xs">
                New
              </Badge> */}
            </Box>
            <Box
              as={RouterLink}
              to="/documents"
              py={2}
              px={3}
              _hover={{ color: hoverColor, textDecoration: "none" }}
              {...(currentPath === "/documents" ? activeLinkStyle : {})}
            >
              <Flex align="center">
                <Box mr={2}>
                  <svg
                    width="16px"
                    height="16px"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                </Box>
                <Text>Documents</Text>
              </Flex>
            </Box>
            <Box
              as={RouterLink}
              to="/profile"
              py={2}
              px={3}
              _hover={{ color: hoverColor, textDecoration: "none" }}
              {...(currentPath === "/profile" ? activeLinkStyle : {})}
            >
              <Flex align="center">
                <Box mr={2}>
                  <svg
                    width="16px"
                    height="16px"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </Box>
                <Text>My Profile</Text>
              </Flex>
            </Box>
            <Box
              as={RouterLink}
              to="/billing"
              py={2}
              px={3}
              _hover={{ color: hoverColor, textDecoration: "none" }}
              {...(currentPath === "/billing" ? activeLinkStyle : {})}
            >
              <Flex align="center">
                <Box mr={2}>
                  <svg
                    width="16px"
                    height="16px"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                  </svg>
                </Box>
                <Text>Billing & Subscription</Text>
              </Flex>
            </Box>
            {user?.is_superuser && (
              <Box
                as={RouterLink}
                to="/diagnostics"
                py={2}
                px={3}
                _hover={{ color: hoverColor, textDecoration: "none" }}
                {...(currentPath === "/diagnostics" ? activeLinkStyle : {})}
              >
                <Flex align="center">
                  <Box mr={2}>
                    <svg
                      width="16px"
                      height="16px"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                    </svg>
                  </Box>
                  <Text>Database Diagnostics</Text>
                </Flex>
              </Box>
            )}
            <Divider my={2} />
            <Box px={3} py={2} display="flex" justifyContent="center">
              <DarkModeToggle />
            </Box>
            <Divider my={2} />
            <Button
              leftIcon={
                isLoggingOut ? (
                  <Spinner size="sm" />
                ) : (
                  <svg
                    width="16px"
                    height="16px"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                  </svg>
                )
              }
              colorScheme="red"
              size="sm"
              onClick={handleLogout}
              isLoading={isLoggingOut}
              loadingText="Logging out..."
            >
              {isLoggingOut ? "Logging out..." : "Logout"}
            </Button>
              </>
            ) : (
              <>
                <Box
                  as={RouterLink}
                  to="/pricing"
                  py={2}
                  px={3}
                  _hover={{ color: hoverColor, textDecoration: "none" }}
                  {...(currentPath === "/pricing" ? activeLinkStyle : {})}
                >
                  Pricing
                </Box>
                <Box
                  as={RouterLink}
                  to="/login"
                  py={2}
                  px={3}
                  _hover={{ color: hoverColor, textDecoration: "none" }}
                  {...(currentPath === "/login" ? activeLinkStyle : {})}
                >
                  Login
                </Box>
                <Button
                  as={RouterLink}
                  to="/signup"
                  colorScheme="blue"
                  size="sm"
                  mt={2}
                  mx={3}
                  _hover={{ shadow: "md" }}
                >
                  Sign up
                </Button>
              </>
            )}
          </VStack>
        </Box>
      </Collapse>
      </Container>
    </Box>
  );
};

export default Navbar;
